import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { List, Plus, MapPin, Trash2, Edit } from 'lucide-react';

export default function WishlistsSection() {
  const { token } = useAuth();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWishlist, setNewWishlist] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchWishlists();
  }, []);

  const fetchWishlists = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/wishlists`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWishlists(data);
      }
    } catch (error) {
      console.error('Error fetching wishlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWishlist = async () => {
    if (!newWishlist.name.trim()) return;

    setCreating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/wishlists`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newWishlist)
      });

      if (response.ok) {
        const wishlist = await response.json();
        setWishlists([wishlist, ...wishlists]);
        setNewWishlist({ name: '', description: '' });
        setShowCreateModal(false);
      }
    } catch (error) {
      console.error('Error creating wishlist:', error);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Chargement de vos listes d'envie...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Mes listes d'envie ({wishlists.length})</h2>
        
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle liste
            </Button>
          </DialogTrigger>
          
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une nouvelle liste d'envie</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom de la liste *
                </label>
                <Input
                  value={newWishlist.name}
                  onChange={(e) => setNewWishlist(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Mes prochaines escapades"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  Description (optionnel)
                </label>
                <Textarea
                  value={newWishlist.description}
                  onChange={(e) => setNewWishlist(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description de votre liste d'envie..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </Button>
                <Button onClick={createWishlist} disabled={creating || !newWishlist.name.trim()}>
                  {creating ? 'Création...' : 'Créer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {wishlists.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Aucune liste d'envie</h3>
              <p className="text-gray-500 mb-4">
                Créez des listes pour organiser vos spots à visiter !
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Créer ma première liste
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {wishlists.map((wishlist) => (
            <Card key={wishlist.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{wishlist.name}</CardTitle>
                    {wishlist.description && (
                      <p className="text-sm text-gray-600 mt-1">{wishlist.description}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <Badge variant="secondary">
                    <MapPin className="h-3 w-3 mr-1" />
                    {wishlist.spot_ids?.length || 0} spots
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {new Date(wishlist.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <Button variant="outline" size="sm">
                    Voir les spots
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}